import { create } from 'zustand'
import type { Tweet, FeedFilters, PaginatedResponse } from '../types'
import { api } from '../lib/api'

interface FeedState {
  tweets: Tweet[]
  filters: FeedFilters
  isLoading: boolean
  error: string | null
  hasMore: boolean
  page: number
  autoScroll: boolean

  // Actions
  fetchTweets: (reset?: boolean) => Promise<void>
  addTweet: (tweet: Tweet) => void
  setFilters: (filters: Partial<FeedFilters>) => void
  clearFilters: () => void
  setAutoScroll: (enabled: boolean) => void
  markAsAlerted: (tweetId: number) => void
}

export const useFeedStore = create<FeedState>((set, get) => ({
  tweets: [],
  filters: {},
  isLoading: false,
  error: null,
  hasMore: true,
  page: 1,
  autoScroll: true,

  fetchTweets: async (reset = false) => {
    const { filters, page, isLoading } = get()

    // Prevent concurrent fetches
    if (isLoading) return

    const currentPage = reset ? 1 : page

    set({ isLoading: true, error: null })

    try {
      const response = await api.get<PaginatedResponse<Tweet>>('/api/tweets', {
        ...filters,
        page: currentPage,
        limit: 50,
      })

      if (response.success && response.data) {
        set((state) => ({
          tweets: reset ? response.data!.items : [...state.tweets, ...response.data!.items],
          hasMore: response.data!.hasMore,
          page: currentPage + 1,
          isLoading: false,
        }))
      } else {
        // On error, stop trying to load more
        set({ error: response.error || 'Failed to fetch tweets', isLoading: false, hasMore: false })
      }
    } catch (err) {
      // On error, stop trying to load more
      set({ error: 'Network error', isLoading: false, hasMore: false })
    }
  },

  addTweet: (tweet) => {
    set((state) => {
      // Check if tweet already exists
      if (state.tweets.some((t) => t.tweet_id === tweet.tweet_id)) {
        return state
      }
      // Add to beginning of list
      return { tweets: [tweet, ...state.tweets] }
    })
  },

  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      page: 1,
      tweets: [],
      hasMore: true,
    }))
  },

  clearFilters: () => {
    set({ filters: {}, page: 1, tweets: [], hasMore: true })
  },

  setAutoScroll: (enabled) => {
    set({ autoScroll: enabled })
  },

  markAsAlerted: (tweetId) => {
    set((state) => ({
      tweets: state.tweets.map((t) =>
        t.id === tweetId ? { ...t, alerted: true } : t
      ),
    }))
  },
}))
